import { useLanguage } from "../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Clock, Sparkles, ChevronDown } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

export const HeroSection = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToService = () => {
    document.getElementById("service")?.scrollIntoView({ behavior: "smooth" });
  };

  const trustBadges = [
    { icon: Shield, text: t("hero.trustBadge1") },
    { icon: Clock, text: t("hero.trustBadge2") },
    { icon: Sparkles, text: t("hero.trustBadge3") },
  ];

  const darkSlogan =
    language === "fr"
      ? "🌙 Avec Éco-Bain, c’est le jour et la nuit."
      : "🌙 With Éco-Bain, it’s night and day.";

  const heroSubtitle =
    language === "fr"
      ? "Réémaillage de baignoire à Montréal dès 385 $. Intervention sur place en 90 minutes, sans démolition. Baignoire utilisable dès le lendemain."
      : "Bathtub reglazing in Montreal from $385. On-site service in 90 minutes, with no demolition. Bathtub usable the next day.";

  return (
    <section className="relative min-h-screen overflow-hidden pt-16 sm:pt-20 lg:pt-24 pb-28">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-white dark:from-[#070b12] dark:via-[#070b12] dark:to-emerald-900/10" />
      {/* Keep the green atmosphere only in dark mode */}
      <div className="hidden dark:block absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#5cb85c]/10 to-transparent" />

      {/* Decorative blobs (dark mode only) */}
      <div className="hidden dark:block absolute top-40 left-10 w-72 h-72 bg-[#5cb85c]/15 rounded-full blur-3xl" />
      <div className="hidden dark:block absolute bottom-20 right-20 w-96 h-96 bg-emerald-900/20 rounded-full blur-3xl" />

      <div className="container-width relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left */}
          <div className="text-center lg:text-left">
            {/* Badge switch (light -> t(hero.badge), dark -> slogan) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-[#5cb85c]/10 text-[#5cb85c] dark:bg-[#5cb85c]/15 text-sm font-semibold mb-6 overflow-hidden"
            >
              <AnimatePresence mode="wait">
                {isDark ? (
                  <motion.span
                    key="dark-slogan"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="flex items-center gap-2"
                  >
                    {darkSlogan}
                  </motion.span>
                ) : (
                  <motion.span
                    key="light-badge"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="flex items-center gap-2"
                  >
                    <Sparkles size={16} />
                    {t("hero.badge")}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
              {t("hero.title")}{" "}
              <span className="text-[#5cb85c]">{t("hero.titleHighlight")}</span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0">
              {heroSubtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="btn-primary text-lg"
              >
                {t("hero.cta")}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToService}
                className="btn-secondary text-lg"
              >
                {t("hero.ctaSecondary")}
              </motion.button>
            </div>

            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              {trustBadges.map((badge, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-white/5 rounded-full shadow-md border border-slate-100 dark:border-white/10"
                >
                  <badge.icon size={18} className="text-[#5cb85c]" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {badge.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/images/hero-bathroom-768.webp 768w, /images/hero-bathroom-1280.webp 1280w"
                  sizes="(max-width: 1023px) 100vw, 50vw"
                />
                <img
                  src="/images/hero-bathroom-1280.webp"
                  alt={language === "fr" ? "Salle de bain restaurée par Éco-Bain" : "Bathroom restored by Éco-Bain"}
                  width="1280"
                  height="853"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </picture>

              <div className="absolute bottom-6 left-6 right-6 bg-white/95 dark:bg-black/40 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-transparent dark:border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-300">
                      {t("hero.resultLabel")}
                    </p>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      {t("hero.resultText")}
                    </p>
                  </div>
                  <div className="w-14 h-14 bg-[#5cb85c] rounded-xl flex items-center justify-center">
                    <Shield className="text-white" size={28} />
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -top-4 -right-4 bg-white dark:bg-black/40 rounded-2xl p-4 shadow-xl hidden lg:block border border-transparent dark:border-white/10">
              <p className="text-2xl font-bold text-[#5cb85c]">{t("hero.trustBadge1")}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {t("hero.trustBadgeLabel")}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center text-slate-400 dark:text-slate-300"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown size={26} />
        </motion.div>
      </motion.div>
    </section>
  );
};
